<template>
  <div class="card is-smallshadow">
    <div class="card-body py-3">
      <h2 class="heading-small text-grey mb-2">
        {{ $t('adviserSettings.contactData') }}
      </h2>

      <div class="width-narrow">
        <validation-observer
          ref="adviser-settings-contact-form"
          tag="form">
          <div class="form-group">
            <label for="name">{{ $t('user.firstName') }}</label>

            <validation-provider
              rules="required"
              name="name"
              v-slot="{ errors }">
              <f-input
                v-model="data.name"
                :state="errors.length ? 'invalid' : null"/>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label for="surname">{{ $t('user.lastName') }}</label>

            <validation-provider
              rules="required"
              name="surname"
              v-slot="{ errors }">
              <f-input
                v-model="data.surname"
                :state="errors.length ? 'invalid' : null"/>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label for="email">{{ $t('user.emailAddress') }}</label>

            <validation-provider
              rules="required|email"
              name="email"
              v-slot="{ errors }">
              <f-input
                :disabled="true"
                v-model="data.email"
                type="email"
                :state="errors.length ? 'invalid' : null"/>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label for="phone">{{ $t('user.phoneNumber') }}</label>

            <validation-provider
              rules="required|min:9|tel"
              name="phone"
              mode="eager"
              v-slot="{ errors }">
              <vue-tel-input
                ref="tel-input"
                :key="defaultCountyCode"
                :defaultCountry="defaultCountyCode"
                :disabledFetchingCountry="true"
                :dynamicPlaceholder="false"
                :placeholder="''"
                :validCharactersOnly="true"
                :inputOptions="{
                  showDialCode:true
                }"
                :class="{
                  'f-input form-control': true,
                  'is-valid': !errors.length,
                  'is-invalid': errors.length
                }"
                v-model="data.phone"
                :state="errors.length ? 'invalid' : null">
              </vue-tel-input>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <template v-if="langId === 2">
            <div class="form-group">
              <label for="region">{{ $t('user.region') }}</label>
              <validation-provider
                tag="div"
                rules="required"
                name="region"
                v-slot="{ errors }">
                <div
                  :class="{
                    'form-select': true,
                    'is-invalid': errors.length
                  }">
                  <v-select
                    id="region"
                    :options="allRegions"
                    v-model="data.uk_region_id"
                    :reduce="(region) => region.id"
                    :clearable="false"
                    :searchable="false"
                    :placeholder="$t('user.selectRegion')"
                    label="name">
                  </v-select>
                </div>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>
            <div class="form-group">
              <label for="county">{{ $t('user.county') }}</label>
              <validation-provider
                tag="div"
                rules="required"
                name="county"
                v-slot="{ errors }">
                <div
                  :class="{
                    'form-select': true,
                    'is-invalid': errors.length
                  }">
                  <v-select
                    id="county"
                    :options="countiesForRegion"
                    :disabled="!data.uk_region_id"
                    v-model="data.uk_county_id"
                    :reduce="(county) => county.id"
                    :clearable="false"
                    :searchable="false"
                    :placeholder="$t('user.selectCounty')"
                    label="name">
                  </v-select>
                </div>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>
          </template>
          <template v-else>
            <div class="form-group">
              <label for="province">{{ $t('user.province') }}</label>
              <validation-provider
                tag="div"
                rules="required"
                name="province"
                v-slot="{ errors }">
                <div
                  :class="{
                    'form-select': true,
                    'is-invalid': errors.length
                  }">
                  <v-select
                    id="province"
                    :options="allProvinces"
                    v-model="data.province_id"
                    :reduce="(province) => province.id"
                    :clearable="false"
                    :searchable="false"
                    :placeholder="$t('user.selectProvince')"
                    label="name">
                  </v-select>
                </div>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>
          </template>

          <div class="form-group">
            <label for="address">{{ $t('user.address') }}</label>

            <validation-provider
              rules="required"
              name="address"
              v-slot="{ errors }">
              <f-input
                v-model="data.address"
                :state="errors.length ? 'invalid' : null"/>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label for="city">{{ $t('user.city') }}</label>

            <validation-provider
              rules="required"
              name="city"
              v-slot="{ errors }">
              <f-input
                v-model="data.city"
                :state="errors.length ? 'invalid' : null"/>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div
            v-if="langId === 2"
            class="form-group">
            <label for="postal-code-gb">{{ $t('user.postalCode') }}</label>

            <validation-provider
              rules="required|postal-code-gb"
              name="postal-code-gb"
              v-slot="{ errors }">
              <f-input
                v-model="data.post_code"
                :state="errors.length ? 'invalid' : null"/>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>
          <div
            v-else
            class="form-group">
            <label for="postal-code">{{ $t('user.postalCode') }}</label>

            <validation-provider
              rules="required|postal-code"
              name="postal-code"
              v-slot="{ errors }">
              <f-input
                v-model="data.post_code"
                v-mask="'##-###'"
                :state="errors.length ? 'invalid' : null"/>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>
        </validation-observer>
      </div>
    </div>
    <div class="card-footer pt-3 pb-4 text-right">
      <f-button
        theme="primary"
        big
        @click.prevent="validateForm">
        {{ $t('ui.save') }}
      </f-button>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import FormUtils from '@/utils/FormUtils.js';

export default {
  name: 'adviser-settings-contact-info',
  props: {
    address: {
      type: String
    },
    allProvinces: {
      type: Array
    },
    allRegions: {
      type: Array
    },
    city: {
      type: String
    },
    email: {
      type: String
    },
    mobilePhone: {
      type: String
    },
    name: {
      type: String
    },
    phone: {
      type: String
    },
    postCode: {
      type: String
    },
    provinceId: {
      type: Number
    },
    surname: {
      type: String
    },
    ukCountyId: {
      type: Number
    },
    ukRegionId: {
      type: Number
    }
  },
  computed: {
    ...mapGetters({
      langId: 'getUserLanguageId'
    }),
    countiesForRegion () {
      if (!this.data.uk_region_id) {
        return [];
      }

      let selectedRegion = this.allRegions.find(item => item.id === this.data.uk_region_id);
      if (!selectedRegion) {
        return [];
      }

      return selectedRegion.counties;
    }
  },
  watch: {
    'data.uk_region_id': function (newValue, oldValue) {
      if (!oldValue && newValue) {
        return;
      }
      this.data.uk_county_id = 0;
    }
  },
  data() {
    return {
      data: {
        address: '',
        city: '',
        email: null,
        name: '',
        phone: '',
        post_code: '',
        province_id: 0,
        surname: '',
        uk_county_id: null,
        uk_region_id: null
      },
      defaultCountyCode: null
    }
  },
  mounted () {
    Vue.set(this, 'defaultCountyCode', this.$store.getters['getUserPhoneCountryCode']);
    this.data.address = this.address;
    this.data.city = this.city;
    this.data.email = this.email;
    this.data.name = this.name;
    this.data.phone = this.phone;
    this.data.post_code = this.postCode;
    this.data.province_id = this.provinceId;
    this.data.surname = this.surname;
    this.data.uk_county_id = this.ukCountyId;
    this.data.uk_region_id = this.ukRegionId;
  },
  methods: {
    submitForm () {
      this.$bus.$emit('update-adviser-settings', this.prepareFormDataToSend());
    },
    validateForm () {
      FormUtils.validate(this.$refs['adviser-settings-contact-form'], this.submitForm);
    },
    prepareFormDataToSend () {
      let formDataToSend = { ...this.data };
      let telInput = this.$refs['tel-input'];
      if (telInput) {
        formDataToSend.phone = telInput.phoneObject.number.international;
      }

      return formDataToSend;
    }
  }
}
</script>
